import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from "react-router-dom";

const Footer = () => {
  const goldColor = "#fc7303";
  const currentYear = new Date().getFullYear();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [visible, setVisible] = useState(false);
  const footerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (footerRef.current) observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, []);

  const now = new Date();
  const day = now.getDay(); // 0 = Sunday, 1 = Monday ... 6 = Saturday
  const hour = now.getHours();

  // Open: Monday-Saturday, 9 AM - 8 PM
  const isOpen = day >= 1 && day <= 6 && hour >= 9 && hour < 20;

  return (
    <footer ref={footerRef} style={{ position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Barlow:wght@300;400;500;600&display=swap');
        @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css');

        :root {
          --gold: #fc7303;
          --gold-dim: rgba(252,115,3,0.13);
          --gold-glow: rgba(252,115,3,0.35);
          --white: #ffffff;
          --off-white: #e8e0d8;
          --muted: rgba(232,224,216,0.55);
          --border: rgba(252,115,3,0.18);
          --card-bg: rgba(255,255,255,0.03);
        }

        .srf-dev {
          position: relative;
          z-index: 1;
          width: 100%;
          text-align: center;
          padding: 14px 20px;
          border-top: 1px solid rgba(255,255,255,0.05);
          font-size: 0.72rem;
          letter-spacing: 0.5px;
          color: rgba(255,255,255,0.38);
        }

        .srf-dev-links {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-left: 4px;
        }

        .srf-dev a {
          color: var(--gold);
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .srf-dev a:hover { color: #fff; }

        .srf {
          background: #000;
          color: var(--off-white);
          font-family: 'Barlow', sans-serif;
          border-top: 2px solid var(--gold);
          position: relative;
        }
        .srf::before {
          content: '';
          position: absolute; inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E");
          pointer-events: none; z-index: 0;
        }

        .srf-row {
          position: relative; z-index: 1;
          display: grid;
          grid-template-columns: 1.05fr 0.85fr 1fr 1.1fr;
          gap: 0;
          padding: 36px 4% 30px;
          align-items: start;
        }

        .srf-col {
          padding: 0 24px;
          border-right: 1px solid var(--border);
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s cubic-bezier(.16,1,.3,1), transform 0.6s cubic-bezier(.16,1,.3,1);
        }
        .srf-col:first-child { padding-left: 0; }
        .srf-col:last-child  { border-right: none; padding-right: 0; }
        .srf-col.show { opacity: 1; transform: translateY(0); }
        .srf-col:nth-child(1) { transition-delay: 0s; }
        .srf-col:nth-child(2) { transition-delay: 0.1s; }
        .srf-col:nth-child(3) { transition-delay: 0.2s; }
        .srf-col:nth-child(4) { transition-delay: 0.3s; }

        .srf-heading {
          font-family: 'Oswald', sans-serif;
          font-size: 0.78rem;
          font-weight: 600;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          color: var(--white);
          margin: 0 0 14px;
          padding-bottom: 9px;
          position: relative;
        }
        .srf-heading::after {
          content: '';
          position: absolute; left: 0; bottom: 0;
          height: 2px; width: 28px;
          background: var(--gold); border-radius: 2px;
          transition: width 0.4s ease;
        }
        .srf-col:hover .srf-heading::after { width: 48px; }

        .srf-brand {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }
        .srf-brand-name {
          font-family: 'Oswald', sans-serif;
          font-size: 1.1rem;
          font-weight: 700;
          letter-spacing: 1.5px;
          margin: 0;
          color: #fff;
        }
        .srf-brand-sub {
          font-size: 0.65rem;
          letter-spacing: 1px;
          color: var(--gold);
          text-transform: uppercase;
          margin: 2px 0 0;
        }
        .srf-tagline {
          font-size: 0.8rem;
          color: var(--muted);
          line-height: 1.5;
          margin: 14px 0;
        }

        .srf-hours {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.75rem;
          color: #fff;
          background: rgba(255,255,255,0.06);
          padding: 6px 12px;
          border-radius: 20px;
          border: 1px solid rgba(255,255,255,0.1);
          margin-bottom: 16px;
        }
        .srf-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #22c55e;
          box-shadow: 0 0 8px #22c55e;
        }
        .srf-hours.closed .srf-dot {
          background: #ef4444;
          box-shadow: 0 0 8px #ef4444;
        }

        .srf-socials { display: flex; gap: 10px; margin-top: 10px; }
        .srf-social {
          width: 34px; height: 34px;
          border-radius: 50%;
          background: rgba(255,255,255,0.06);
          border: 1px solid var(--border);
          color: #fff;
          display: flex; align-items: center; justify-content: center;
          text-decoration: none;
          transition: all 0.3s;
        }
        .srf-social:hover {
          background: var(--gold);
          color: #000;
          transform: translateY(-2px);
        }

        .srf-product {
          display: block;
          color: var(--muted);
          text-decoration: none;
          font-size: 0.82rem;
          padding: 5px 0;
          transition: color 0.3s, transform 0.3s;
        }
        .srf-product:hover {
          color: var(--gold);
          transform: translateX(4px);
        }

        .srf-phone-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          text-decoration: none;
          background: rgba(255,255,255,0.04);
          padding: 10px 14px;
          border-radius: 10px;
          border: 1px solid var(--border);
          margin-bottom: 14px;
          transition: background 0.3s, border-color 0.3s;
        }
        .srf-phone-btn:hover {
          background: rgba(252,115,3,0.1);
          border-color: var(--gold);
        }
        .srf-pulse-ring {
          width: 36px; height: 36px;
          border-radius: 50%;
          border: 1px solid var(--gold);
          display: flex; align-items: center; justify-content: center;
        }
        .srf-clabel { font-size: 0.65rem; color: var(--gold); text-transform: uppercase; font-weight: 700; letter-spacing: 1px; display: block; }
        .srf-cval { font-size: 0.95rem; color: #fff; font-weight: 700; display: block; }
        .srf-chours { font-size: 0.7rem; color: var(--muted); display: block; margin-top: 2px; }

        .srf-crow {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 0.8rem;
          color: var(--muted);
          margin-bottom: 12px;
        }
        .srf-cicon { color: var(--gold); margin-top: 3px; font-size: 0.9rem; }
        .srf-ctext a { color: var(--muted); text-decoration: none; transition: color 0.3s; }
        .srf-ctext a:hover { color: var(--gold); }

        .srf-map-wrap {
          height: 150px;
          border-radius: 10px;
          overflow: hidden;
          position: relative;
          border: 1px solid rgba(255,255,255,0.1);
        }
        .srf-map-badge {
          position: absolute;
          top: 6px;
          right: 6px;
          background: rgba(0,0,0,0.7);
          color: #fff;
          font-size: 0.65rem;
          padding: 2px 8px;
          border-radius: 4px;
          z-index: 2;
          pointer-events: none;
        }

        .srf-dir-btn {
          display: inline-flex; align-items: center; gap: 6px;
          margin-top: 10px; padding: 7px 14px;
          background: transparent;
          border: 1px solid var(--gold); border-radius: 7px;
          color: var(--gold); font-size: 0.68rem; font-weight: 600;
          letter-spacing: 1px; text-decoration: none; text-transform: uppercase;
          transition: background 0.3s, color 0.3s, transform 0.3s;
        }
        .srf-dir-btn:hover {
          background: var(--gold); color: #000;
          transform: translateY(-1px);
        }

        .srf-bottom {
          position: relative; z-index: 1;
          border-top: 1px solid rgba(255,255,255,0.06);
          margin: 0 4%; padding: 14px 0;
          display: flex; align-items: center;
          justify-content: space-between; flex-wrap: wrap; gap: 8px;
        }
        .srf-copy { font-size: 0.7rem; color: rgba(255,255,255,0.38); margin: 0; }
        .srf-blinks { display: flex; gap: 20px; }
        .srf-blink {
          font-size: 0.7rem; color: rgba(255,255,255,0.45);
          text-decoration: none; transition: color 0.3s;
        }
        .srf-blink:hover { color: var(--gold); }

        .srf-particle {
          position: absolute; width: 2px; height: 2px;
          background: var(--gold); border-radius: 50%;
          opacity: 0; pointer-events: none;
          animation: srfFloat linear infinite;
        }
        @keyframes srfFloat {
          0%   { transform: translateY(0) translateX(0); opacity: 0; }
          10%  { opacity: 0.5; }
          90%  { opacity: 0.25; }
          100% { transform: translateY(-100px) translateX(15px); opacity: 0; }
        }

        @media (max-width: 992px) {
          .srf-row { grid-template-columns: 1fr 1fr; gap: 24px; padding: 28px 5% 20px; }
          .srf-col { border-right: none !important; padding: 10px 0; }
        }
        @media (max-width: 576px) {
          .srf-row { grid-template-columns: 1fr; padding: 24px 5% 16px; }
          .srf-col { border-bottom: 1px solid var(--border); padding-bottom: 20px; }
          .srf-col:last-child { border-bottom: none; }
          .srf-bottom { flex-direction: column; text-align: center; }
          .srf-blinks { justify-content: center; }
        }
      `}</style>

      <div className="srf">
        {[...Array(6)].map((_, i) => (
          <span key={i} className="srf-particle" style={{
            left: `${10 + i * 16}%`,
            bottom: `${15 + (i % 3) * 18}%`,
            animationDuration: `${4.5 + i * 1.2}s`,
            animationDelay: `${i * 0.9}s`,
          }} />
        ))}

        <div className="srf-row">

          {/* COL 1 — BRAND */}
          <div className={`srf-col ${visible ? 'show' : ''}`}>
            <div className="srf-brand">
              <svg width="30" height="38" viewBox="0 0 100 120" style={{ overflow: 'visible' }}>
                <rect x="10" y="5" width="80" height="110" rx="2" fill="none" stroke={goldColor} strokeWidth="5" />
                <path d="M15 10 L85 7 L85 113 L15 110 Z" fill={goldColor} />
                <circle cx="74" cy="60" r="5" fill="#000" />
              </svg>
              <div>
                <h4 className="srf-brand-name">SREE RAGHAVENDRA</h4>
                <p className="srf-brand-sub">Hardware, Plywoods &amp; Doors</p>
              </div>
            </div>

            <p className="srf-tagline">
              Premium wooden doors, durable BWP plywoods, decorative laminates &amp; luxury hardware in Rajahmundry. Serving homeowners, builders, and architects since 2008.
            </p>

            <div className={`srf-hours ${isOpen ? "open" : "closed"}`}>
              <span className="srf-dot" />
              Mon – Sat &nbsp;·&nbsp; 9am – 8pm (Sunday Closed)
            </div>

            <div className="srf-socials">
              <a href="https://www.instagram.com/sree_raghavendra_doors?igsh=MXRocjMxNmVraG92dw%3D%3D" className="srf-social" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <i className="fa-brands fa-instagram" />
              </a>
              <a
                href="https://wa.me/918374771149?text=Hi,%20I%20would%20like%20to%20know%20more%20about%20your%20products%20in%20Rajahmundry."
                className="srf-social"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
              >
                <i className="fa-brands fa-whatsapp" />
              </a>
              <a href="tel:+918374771149" className="srf-social" aria-label="Call">
                <i className="fa-solid fa-phone" />
              </a>
            </div>
          </div>

          {/* COL 2 — PRODUCTS */}
          <div className={`srf-col ${visible ? 'show' : ''}`}>
            <h5 className="srf-heading">Our Products</h5>

            {[
              { name: 'Teak Wood Doors', path: '/doors?category=Teak' },
              { name: 'WPVC Waterproof Doors', path: '/doors?category=WPVC' },
              { name: 'Sunmica & Laminate Doors', path: '/doors?category=Sunmica' },
              { name: 'Prelaminated Doors', path: '/doors?category=Prelaminated' },
              { name: 'Grewing Textured Doors', path: '/doors?category=Grewing' },
              { name: 'Complete Door Catalogue (3D)', path: '/doors?category=All' },
            ].map((p) => (
              <NavLink
                key={p.name}
                to={p.path}
                className="srf-product"
              >
                {p.name}
              </NavLink>
            ))}
          </div>

          {/* COL 3 — CONTACT */}
          <div className={`srf-col ${visible ? 'show' : ''}`}>
            <h5 className="srf-heading">Get In Touch</h5>

            <a href="tel:+918374771149" className="srf-phone-btn">
              <div className="srf-pulse-ring">
                <i className="fa-solid fa-phone" style={{ color: goldColor, fontSize: '14px' }}></i>
              </div>
              <div>
                <span className="srf-clabel">Primary Store Contact</span>
                <span className="srf-cval">+91 83747 71149</span>
                <span className="srf-chours">Mon – Sat · 9am – 8pm</span>
              </div>
            </a>

            <div className="srf-crow">
              <div className="srf-cicon"><i className="fa-solid fa-mobile-screen" /></div>
              <div className="srf-ctext">
                <span className="srf-clabel">Alternate Number</span>
                <a href="tel:+919849659309">+91 98496 59309</a>
              </div>
            </div>

            <div className="srf-crow">
              <div className="srf-cicon"><i className="fa-solid fa-envelope" /></div>
              <div className="srf-ctext">
                <span className="srf-clabel">Email</span>
                <a href="mailto:koushikmattapalli9550@gmail.com">koushikmattapalli9550@gmail.com</a>
              </div>
            </div>

            <div className="srf-crow">
              <div className="srf-cicon"><i className="fa-solid fa-location-dot" /></div>
              <div className="srf-ctext">
                <span className="srf-clabel">Showroom Address</span>
                Morampudi - Kesavaram Rd, Aditya Nagar,<br />
                Rajamahendravaram, AP – 533107
              </div>
            </div>
          </div>

          {/* COL 4 — MAP */}
          <div className={`srf-col ${visible ? 'show' : ''}`}>
            <h5 className="srf-heading">Showroom Location</h5>
            <div className="srf-map-wrap">
              <span className="srf-map-badge">📍 Live Map</span>
              <iframe
                title="Sree Raghavendra Doors Location"
                src="https://www.google.com/maps?q=Sree+Raghavendra+Doors+Hardware+Aditya+Nagar+Rajamahendravaram+Andhra+Pradesh&output=embed"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                onLoad={() => setMapLoaded(true)}
                style={{
                  opacity: mapLoaded ? 1 : 0.6,
                  transition: 'opacity 0.5s',
                  width: '100%',
                  height: '100%',
                  border: '0',
                }}
              />
            </div>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Sree+Raghavendra+Doors+Hardware+Aditya+Nagar+Rajamahendravaram+Andhra+Pradesh"
              target="_blank"
              rel="noopener noreferrer"
              className="srf-dir-btn"
            >
              <i className="fa-solid fa-diamond-turn-right" />
              Get Showroom Directions
            </a>
          </div>

        </div>

        {/* ── BOTTOM BAR WITH REACT ROUTER NAVLINKS ── */}
        <div className="srf-bottom">
          <p className="srf-copy">© {currentYear} Sree Raghavendra Doors, Plywood &amp; Hardware. Rajamahendravaram.</p>
          <div className="srf-blinks">
            <NavLink to="/privacy-policy" className="srf-blink">Privacy Policy</NavLink>
            <NavLink to="/terms-and-conditions" className="srf-blink">Terms of Service</NavLink>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
